// src/calculation/CalculationWorkspaceModal.tsx
//
// BekalNanti — Milestone P5: Deterministic Calculation Workspace Modal
// Transparent, reproducible calculation results without subjective judgment or fake precision.
// Boundary: Calculates numbers honestly; hands off to P6 Reality Check for evaluation.

import React, { useState, useMemo } from 'react'
import {
  X,
  Calculator,
  TrendingUp,
  AlertTriangle,
  Info,
  Calendar,
  Layers,
  ArrowRight,
  Sliders,
  DollarSign,
  ShieldCheck,
} from 'lucide-react'
import { GoalRecord, JourneyData } from '../journey/types'
import { runGoalCalculation } from '../lib/calculationService'
import { DEFAULT_ANNUAL_INFLATION_RATE } from '../lib/calculation/educationCalculation'
import { formatIDR, formatCompactIDR } from '../lib/financialCalculation'

interface CalculationWorkspaceModalProps {
  isOpen: boolean
  onClose: () => void
  goal: GoalRecord | null
  journey: JourneyData
  onProceedToRealityCheck?: () => void
}

export const CalculationWorkspaceModal: React.FC<CalculationWorkspaceModalProps> = ({
  isOpen,
  onClose,
  goal,
  journey,
  onProceedToRealityCheck,
}) => {
  const [inflationRate, setInflationRate] = useState<number>(DEFAULT_ANNUAL_INFLATION_RATE)
  const [activeTab, setActiveTab] = useState<'overview' | 'costs' | 'assumptions'>('overview')

  // Run calculation deterministically on current state and selected assumptions
  const result = useMemo(() => {
    if (!goal) return null
    return runGoalCalculation(goal, journey, { annualInflationRate: inflationRate })
  }, [goal, journey, inflationRate])

  if (!isOpen || !goal) return null

  const isCalculable = result?.isCalculable ?? false

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="calc-modal-title"
      className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-200"
    >
      <div className="bg-white rounded-3xl shadow-2xl border border-slate-200 w-full max-w-3xl max-h-[92vh] flex flex-col overflow-hidden text-left">
        {/* Header */}
        <div className="px-6 py-5 border-b border-slate-100 flex items-start justify-between bg-slate-50/50">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-2xl bg-teal-50 border border-teal-200/80 flex items-center justify-center text-[#0F766E] flex-shrink-0 mt-0.5">
              <Calculator size={20} strokeWidth={2.4} />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="text-[11px] font-bold uppercase tracking-wider text-teal-700 bg-teal-100/60 px-2 py-0.5 rounded-full">
                  Tahap 5 • Kalkulasi Deterministik
                </span>
                {result?.assumptions.targetYear && (
                  <span className="text-xs text-slate-500 font-medium">
                    Sasaran Thn {result.assumptions.targetYear}
                  </span>
                )}
              </div>
              <h2 id="calc-modal-title" className="text-lg font-bold text-slate-900 mt-1">
                Kalkulasi Perencanaan: {goal.name}
              </h2>
              <p className="text-xs text-slate-500 mt-0.5">
                Hasil perhitungan proyeksi kebutuhan bekal berbasis data dan asumsi yang Anda masukkan.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Tutup"
            className="p-2 rounded-xl text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors cursor-pointer"
          >
            <X size={18} />
          </button>
        </div>

        {/* Tab Navigation */}
        <div className="flex border-b border-slate-100 px-6 bg-white gap-2">
          <button
            type="button"
            onClick={() => setActiveTab('overview')}
            className={`py-3 px-3 text-xs font-semibold border-b-2 flex items-center gap-2 transition-colors cursor-pointer ${
              activeTab === 'overview'
                ? 'border-[#0EA5A4] text-[#0F766E]'
                : 'border-transparent text-slate-500 hover:text-slate-800'
            }`}
          >
            <Layers size={14} />
            <span>1. Ringkasan Perhitungan</span>
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('costs')}
            className={`py-3 px-3 text-xs font-semibold border-b-2 flex items-center gap-2 transition-colors cursor-pointer ${
              activeTab === 'costs'
                ? 'border-[#0EA5A4] text-[#0F766E]'
                : 'border-transparent text-slate-500 hover:text-slate-800'
            }`}
          >
            <DollarSign size={14} />
            <span>2. Rincian Komponen Biaya ({result?.costs.normalizedItems.length || 0})</span>
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('assumptions')}
            className={`py-3 px-3 text-xs font-semibold border-b-2 flex items-center gap-2 transition-colors cursor-pointer ${
              activeTab === 'assumptions'
                ? 'border-[#0EA5A4] text-[#0F766E]'
                : 'border-transparent text-slate-500 hover:text-slate-800'
            }`}
          >
            <Sliders size={14} />
            <span>3. Asumsi & Formula</span>
          </button>
        </div>

        {/* Modal Body */}
        <div className="p-6 overflow-y-auto flex-1 space-y-5">
          {!isCalculable ? (
            <div className="p-4 rounded-2xl bg-amber-50 border border-amber-200 text-amber-900 space-y-3">
              <div className="flex items-center gap-2 font-bold text-sm">
                <AlertTriangle size={18} className="text-amber-600" />
                <span>{result?.statusLabel || 'Data Belum Siap Dihitung'}</span>
              </div>
              <p className="text-xs leading-relaxed text-amber-800">{result?.statusMessage}</p>
              {result?.blockingReasons && result.blockingReasons.length > 0 && (
                <ul className="text-xs space-y-1 list-disc list-inside text-amber-700 bg-white/70 p-3 rounded-xl border border-amber-200/60">
                  {result.blockingReasons.map((reason, idx) => (
                    <li key={idx}>{reason}</li>
                  ))}
                </ul>
              )}
            </div>
          ) : (
            <>
              {/* TAB 1: OVERVIEW */}
              {activeTab === 'overview' && result && (
                <div className="space-y-5">
                  {/* Unknown notice if any */}
                  {(result.costs.unknownCostCount > 0 || result.funding.unknownFundingCount > 0) && (
                    <div className="p-3 rounded-xl bg-blue-50/80 border border-blue-200/80 flex items-start gap-2.5 text-xs text-blue-900">
                      <Info size={16} className="text-blue-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <span className="font-bold">Komponen Belum Pasti: </span>
                        <span>
                          Terdapat {result.costs.unknownCostCount} biaya dan {result.funding.unknownFundingCount} sumber pendanaan berstatus belum diketahui. Nilai tersebut dipisahkan secara transparan dan tidak diasumsikan nol secara sepihak.
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Primary Metrics Grid */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                    {/* Card 1: Total Projected Cost */}
                    <div className="p-4 rounded-2xl bg-slate-50 border border-slate-200/80 space-y-2">
                      <div className="flex items-center justify-between text-xs text-slate-500 font-medium">
                        <span className="flex items-center gap-1.5">
                          <TrendingUp size={14} className="text-teal-600" />
                          Proyeksi Biaya Masa Depan (FV)
                        </span>
                        <span className="text-[11px] font-bold text-teal-700 bg-teal-100/70 px-2 py-0.5 rounded-full">
                          +{(result.assumptions.annualInflationRate * 100).toFixed(0)}%/thn
                        </span>
                      </div>
                      <p className="text-2xl font-extrabold text-slate-900 tracking-tight">
                        {formatIDR(result.costs.totalProjectedCost)}
                      </p>
                      <p className="text-[11px] text-slate-500 leading-tight">
                        Nominal saat ini: <span className="font-semibold">{formatIDR(result.costs.totalNominalCost)}</span> (terhitung inflasi {result.assumptions.yearsUntilTarget} tahun ke {result.assumptions.targetYear}).
                      </p>
                    </div>

                    {/* Card 2: Mapped Funding */}
                    <div className="p-4 rounded-2xl bg-slate-50 border border-slate-200/80 space-y-2">
                      <div className="flex items-center justify-between text-xs text-slate-500 font-medium">
                        <span className="flex items-center gap-1.5">
                          <ShieldCheck size={14} className="text-emerald-600" />
                          Potensi Pendanaan Terpetakan
                        </span>
                        <span className="text-[11px] text-slate-500">
                          {result.funding.confirmedFunding > 0 ? 'Pasti & Rencana' : 'Rencana'}
                        </span>
                      </div>
                      <p className="text-2xl font-extrabold text-emerald-700 tracking-tight">
                        {formatIDR(result.funding.totalPotentialFunding)}
                      </p>
                      <p className="text-[11px] text-slate-500 leading-tight">
                        Pasti: {formatIDR(result.funding.confirmedFunding)} • Rencana/Estimasi: {formatIDR(result.funding.estimatedFunding)}
                      </p>
                    </div>

                    {/* Card 3: Funding Gap */}
                    <div className="p-4 rounded-2xl bg-amber-50/60 border border-amber-200/80 space-y-2">
                      <div className="flex items-center justify-between text-xs text-amber-900 font-medium">
                        <span className="flex items-center gap-1.5">
                          <AlertTriangle size={14} className="text-amber-600" />
                          Gap Kebutuhan Pendanaan
                        </span>
                        <span className="text-[11px] font-bold text-amber-800">
                          {result.gap.hasSurplus ? 'Surplus' : 'Perlu Disiapkan'}
                        </span>
                      </div>
                      <p className="text-2xl font-extrabold text-amber-900 tracking-tight">
                        {result.gap.hasSurplus
                          ? `Surplus ${formatIDR(result.gap.surplusAfterEstimated)}`
                          : formatIDR(result.gap.gapAfterEstimatedFunding)}
                      </p>
                      <p className="text-[11px] text-amber-700 leading-tight">
                        {result.gap.hasSurplus
                          ? 'Total pendanaan terencana mencukupi seluruh proyeksi biaya.'
                          : `Sisa biaya yang belum tertutupi oleh potensi pendanaan terencana.`}
                      </p>
                    </div>

                    {/* Card 4: Required Monthly Saving */}
                    <div className="p-4 rounded-2xl bg-teal-50/60 border border-teal-200/80 space-y-2">
                      <div className="flex items-center justify-between text-xs text-teal-900 font-medium">
                        <span className="flex items-center gap-1.5">
                          <Calendar size={14} className="text-teal-700" />
                          Kebutuhan Tabungan Bulanan
                        </span>
                        <span className="text-[11px] font-bold text-teal-800">
                          {result.assumptions.monthsUntilTarget} Bulan
                        </span>
                      </div>
                      <p className="text-2xl font-extrabold text-[#0F766E] tracking-tight">
                        {formatIDR(result.savingRequirement.requiredMonthlySavingEstimatedGap)}
                        <span className="text-xs font-semibold text-slate-500">/bln</span>
                      </p>
                      <p className="text-[11px] text-teal-800 leading-tight">
                        Formula: Gap ({formatCompactIDR(result.gap.gapAfterEstimatedFunding)}) ÷ {result.assumptions.monthsUntilTarget} bulan horizon menuju mulai studi.
                      </p>
                    </div>
                  </div>

                  {/* Timeline Horizon & Study Duration Explicit Distinction */}
                  <div className="p-3 rounded-xl bg-slate-100/70 border border-slate-200/60 flex items-start gap-2.5 text-xs text-slate-600">
                    <Calendar size={15} className="text-slate-500 flex-shrink-0 mt-0.5" />
                    <p className="text-[11px] leading-relaxed">
                      {result.assumptions.timelineAssumptionNote}
                    </p>
                  </div>

                  {/* Cashflow Capacity vs Requirement Comparison */}
                  <div className="p-4.5 rounded-2xl border border-slate-200 bg-white shadow-sm space-y-3">
                    <h3 className="text-xs font-bold uppercase tracking-wider text-slate-600 flex items-center gap-1.5">
                      <Layers size={14} />
                      Perbandingan dengan Realita Arus Kas (P2)
                    </h3>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-1">
                      <div className="p-3 rounded-xl bg-slate-50 border border-slate-100">
                        <p className="text-[11px] text-slate-500 font-medium">Kapasitas Bersih Bulanan (P2)</p>
                        <p className="text-base font-bold text-slate-900 mt-1">
                          {formatIDR(result.comparison.currentMonthlyNetCapacity)}
                          <span className="text-xs font-normal text-slate-500">/bln</span>
                        </p>
                        <p className="text-[10px] text-slate-400 mt-0.5">Penghasilan - Pengeluaran - Cicilan</p>
                      </div>

                      <div className="p-3 rounded-xl bg-slate-50 border border-slate-100">
                        <p className="text-[11px] text-slate-500 font-medium">Kebutuhan Tabungan Sasaran</p>
                        <p className="text-base font-bold text-[#0F766E] mt-1">
                          {formatIDR(result.comparison.requiredMonthlySaving)}
                          <span className="text-xs font-normal text-slate-500">/bln</span>
                        </p>
                        <p className="text-[10px] text-slate-400 mt-0.5">Untuk menutup gap dalam horizon</p>
                      </div>

                      <div className="p-3 rounded-xl bg-slate-50 border border-slate-100">
                        <p className="text-[11px] text-slate-500 font-medium">Selisih Numerik</p>
                        <p
                          className={`text-base font-bold mt-1 ${
                            result.comparison.numericalDifference >= 0 ? 'text-emerald-700' : 'text-slate-800'
                          }`}
                        >
                          {result.comparison.numericalDifference >= 0
                            ? `+${formatIDR(result.comparison.numericalDifference)}`
                            : formatIDR(result.comparison.numericalDifference)}
                          <span className="text-xs font-normal text-slate-500">/bln</span>
                        </p>
                        <p className="text-[10px] text-slate-400 mt-0.5">
                          {result.comparison.numericalDifference >= 0
                            ? 'Kapasitas bulanan melebihi kebutuhan'
                            : 'Kebutuhan tabungan melebihi kapasitas bulanan'}
                        </p>
                      </div>
                    </div>

                    <p className="text-[11px] text-slate-500 italic pt-1">
                      Catatan: Perbandingan di atas adalah nilai numerik murni. BekalNanti menyajikan angka kalkulasi secara transparan tanpa label subjektif, sebagai bahan pertimbangan objektif bagi keluarga Anda.
                    </p>
                  </div>

                  {/* Boundary Disclaimer */}
                  <div className="p-3.5 rounded-xl bg-slate-100/80 border border-slate-200/70 text-[11px] text-slate-600 flex items-start gap-2">
                    <Info size={16} className="text-slate-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <span className="font-semibold text-slate-800">Batasan Tahap 5: </span>
                      <span>
                        Kalkulasi deterministik ini murni menghitung proyeksi matematis dari data input Anda. Penilaian realitas komparatif, evaluasi fleksibilitas, dan simulasi rute alternatif akan dilakukan pada <strong>Tahap 6 (Reality Check)</strong>.
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* TAB 2: ITEMIZED COSTS BREAKDOWN */}
              {activeTab === 'costs' && result && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="text-xs font-bold text-slate-800">Daftar Komponen Biaya & Normalisasi</h4>
                      <p className="text-[11px] text-slate-500">
                        Durasi studi: {result.assumptions.studyDurationMonths} bulan ({result.assumptions.studyDurationYears} tahun)
                      </p>
                    </div>
                    <span className="text-xs font-bold text-slate-700 bg-slate-100 px-2.5 py-1 rounded-lg">
                      {result.costs.normalizedItems.length} Komponen
                    </span>
                  </div>

                  <div className="border border-slate-200 rounded-2xl overflow-hidden divide-y divide-slate-100">
                    {result.costs.normalizedItems.map((item, idx) => (
                      <div key={item.id || idx} className="p-3.5 bg-white hover:bg-slate-50/50 transition-colors flex items-center justify-between text-xs">
                        <div className="space-y-0.5 max-w-[55%]">
                          <div className="flex items-center gap-2">
                            <span className="font-bold text-slate-900">{item.name}</span>
                            <span
                              className={`text-[10px] font-medium px-2 py-0.2 rounded-full ${
                                item.certainty === 'known'
                                  ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                                  : item.certainty === 'estimated'
                                  ? 'bg-amber-50 text-amber-700 border border-amber-200'
                                  : 'bg-blue-50 text-blue-700 border border-blue-200'
                              }`}
                            >
                              {item.certainty === 'known' ? 'Pasti' : item.certainty === 'estimated' ? 'Estimasi' : 'Belum Tahu'}
                            </span>
                          </div>
                          <p className="text-[11px] text-slate-500">
                            {item.unitAmount !== null
                              ? `${formatIDR(item.unitAmount)} • Pengali durasi: x${item.durationMultiplier}`
                              : 'Nominal belum ditentukan'}
                          </p>
                        </div>

                        <div className="text-right space-y-0.5">
                          {item.totalProjectedCost !== null ? (
                            <>
                              <p className="font-extrabold text-slate-900">{formatIDR(item.totalProjectedCost)}</p>
                              <p className="text-[10px] text-slate-400">Nominal: {formatIDR(item.totalNominalCost || 0)}</p>
                            </>
                          ) : (
                            <span className="text-xs text-slate-400 italic">Belum dapat dihitung</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* TAB 3: ASSUMPTIONS & FORMULAS */}
              {activeTab === 'assumptions' && result && (
                <div className="space-y-5">
                  {/* Inflation Controller */}
                  <div className="p-4.5 rounded-2xl bg-slate-50 border border-slate-200 space-y-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="text-xs font-bold text-slate-900">Asumsi Inflasi Biaya Pendidikan (Planning Assumption)</h4>
                        <p className="text-[11px] text-slate-500">
                          Parameter perencanaan keluarga (standar 5%/thn berdasarkan tren biaya pendidikan Indonesia), bukan ramalan ekonomi makro resmi.
                        </p>
                      </div>
                      <span className="text-sm font-extrabold text-teal-800 bg-teal-100/80 px-2.5 py-1 rounded-xl">
                        {(inflationRate * 100).toFixed(0)}% per tahun
                      </span>
                    </div>

                    <div className="flex items-center gap-2 pt-1">
                      {[0, 0.03, 0.05, 0.07, 0.1].map((rate) => (
                        <button
                          key={rate}
                          type="button"
                          onClick={() => setInflationRate(rate)}
                          className={`py-1.5 px-3 rounded-xl text-xs font-semibold border transition-all cursor-pointer ${
                            inflationRate === rate
                              ? 'bg-[#0EA5A4] text-white border-[#0EA5A4] shadow-sm'
                              : 'bg-white text-slate-700 border-slate-200 hover:border-slate-300'
                          }`}
                        >
                          {(rate * 100).toFixed(0)}%
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Mathematical Formulas Documentation */}
                  <div className="p-4 rounded-2xl border border-slate-200 bg-white space-y-3 text-xs text-slate-700">
                    <h4 className="font-bold text-slate-900 text-xs">Transparansi Rumus & Perhitungan</h4>

                    <div className="space-y-2">
                      <div className="p-2.5 rounded-xl bg-slate-50 border border-slate-100 font-mono text-[11px]">
                        <strong>1. Nilai Masa Depan (FV):</strong> FV = PV × (1 + r)^n
                        <p className="font-sans text-[10px] text-slate-500 mt-0.5">
                          r = {(inflationRate * 100).toFixed(0)}%, n = {result.assumptions.yearsUntilTarget} tahun (dari {result.assumptions.planningAnchorYear} ke {result.assumptions.targetYear}).
                        </p>
                      </div>

                      <div className="p-2.5 rounded-xl bg-slate-50 border border-slate-100 font-mono text-[11px]">
                        <strong>2. Gap Pendanaan:</strong> Gap = Max(0, FV - Total Pendanaan)
                        <p className="font-sans text-[10px] text-slate-500 mt-0.5">
                          Total Pendanaan = {formatIDR(result.funding.totalPotentialFunding)}.
                        </p>
                      </div>

                      <div className="p-2.5 rounded-xl bg-slate-50 border border-slate-100 font-mono text-[11px]">
                        <strong>3. Tabungan Bulanan:</strong> Kebutuhan = Gap ÷ Sisa Bulan ({result.assumptions.monthsUntilTarget} bulan)
                      </div>

                      <div className="p-2.5 rounded-xl bg-slate-50 border border-slate-100 text-[11px]">
                        <strong className="font-mono text-slate-900">4. Perbedaan Horizon Tabungan vs Durasi Studi:</strong>
                        <p className="text-[10px] text-slate-600 mt-1 space-y-1">
                          <span>• <strong>Horizon Menuju Mulai Studi:</strong> {result.assumptions.yearsUntilTarget} tahun ({result.assumptions.monthsUntilTarget} bulan), dari {result.assumptions.planningAnchorYear} ke {result.assumptions.targetYear}. Menjadi pembagi kebutuhan tabungan bulanan.</span><br />
                          <span>• <strong>Durasi Tempuh Studi:</strong> {result.assumptions.studyDurationMonths} bulan ({result.assumptions.studyDurationYears} tahun). Menjadi faktor pengali durasi biaya berkala (SPP per tahun / semester / bulan).</span>
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-100 bg-slate-50/60 flex items-center justify-between">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-xl text-xs font-semibold text-slate-600 hover:bg-slate-200/60 transition-colors cursor-pointer"
          >
            Tutup
          </button>

          {isCalculable && (
            <button
              type="button"
              onClick={() => {
                onClose()
                if (onProceedToRealityCheck) {
                  onProceedToRealityCheck()
                }
              }}
              className="px-5 py-2.5 rounded-xl text-xs font-bold text-white bg-[#0EA5A4] hover:bg-[#0F766E] shadow-sm transition-all flex items-center gap-1.5 cursor-pointer"
            >
              <span>Lanjut ke Reality Check</span>
              <ArrowRight size={14} />
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
